'use strict';
/* ==========================================================================
   core-local/cross.js — "Where Did Those Blue Eyes Come From?": the one-gene heredity model (DESIGN.md §4).
   Pure: no DOM, no engine globals. Runs in node (module.exports: tools/crosscheck.js, tools/contentcheck.js) and in
   the page (window.Cross). Registers itself with the question bank as `cross` when core/bank.js is loaded first
   (lesson.json scripts order).

   The honesty rule: EVERY genotype, Punnett box and percent the lesson shows comes from here. Simple dominance only,
   P × P → F1 only, percents only (facts-blue-eyes.md Correction 3). Percents are exact integers (each box = 25%).

   ---- traits ----
   Cross.TRAITS[key]     {key, gene, letter, dominant, recessive, noun, pic, of, madeUp?, simplified?, mendel?, cite}
                         seedColour Y/y yellow/green · seedShape R/r round/wrinkled · flowerColour V/v violet/white ·
                         podShape I/i inflated/constricted · stemLength T/t tall/short (Mendel's peas, real single
                         genes: facts 2.11, 5.1–5.4, F2 counts in `mendel`) · catEar C/c curled/straight (FLDOE's
                         made-up cat, madeUp: true, facts 5.5) · eyeModel B/b brown/blue (the story's SIMPLIFIED model,
                         simplified: true, facts 2.6 / 5.6: never in a scored item)
                         noun = what the phenotype word describes ('seeds', 'flowers', 'pods', 'plants', 'ears', 'eyes');
                         of = the offspring word ('offspring', 'kittens', 'children'); pic = the picture kind plate.js draws
   Cross.MENDEL          Mendel's seven F2 experiments {expt, trait, dominant, recessive, d, r, ratio} + totals (facts §Safe numbers)
   Cross.traitOf(g)      the trait key a genotype's letter belongs to (null if none / ambiguous)
   Cross.genotypes3(key) the three genotypes of a trait, capital first: ['YY', 'Yy', 'yy']

   ---- one cross ----
   Cross.norm(g)             'yY' → 'Yy' (capital first; throws on a bad genotype)
   Cross.gametes(g)          'Yy' → ['Y', 'y'] (each parent passes ONE allele, 50/50: facts 3.9)
   Cross.phenotype(key, g)   'Yy' → 'yellow' (dominant if any capital letter)
   Cross.square(p1, p2)      {p1, p2, top: p1's alleles, side: p2's alleles, boxes: [4 × {i, row, col, top, side,
                             genotype, phenotype, dominant}]}. Box order is row-major: 0 top-left, 1 top-right,
                             2 bottom-left, 3 bottom-right; col = the TOP (p1) allele, row = the SIDE (p2) allele.
   Cross.genotypes(p1, p2)   {YY: 25, Yy: 50, yy: 25} exact percents (only genotypes that occur)
   Cross.phenotypes(p1, p2)  {yellow: 75, green: 25} exact percents (both phenotype words present, 0 when absent)
   Cross.percent(p1, p2, {kind: 'geno'|'pheno', value})  one exact percent (0, 25, 50, 75, 100)
   Cross.grow(p1, p2, n, seed)  a SEEDED simulation of n offspring: {n, seed, kids: [genotype], counts: {genotype: k},
                             pheno: {word: k}} (each kid = a fresh draw of one gamete per parent: "chances, not counts")
   Cross.binom(n, k, pct)    P(exactly k of n) for a per-offspring chance pct (0–100), exact enough for the checks

   ---- Punnett Lab (content/bank.lab.json) ----
   Cross.steps(item, pool)   the item's steps, built from its spec in the order the pool's notes give:
                             [tiles (p1 on top), tiles (p2 down the side), fill (the 4 boxes), numpad per `ask`, choice …]
                             each {i, type, text, hint, value, answerText, …}:
                               tiles:  {edge: 'top'|'side', parent: 1|2, value: ['Y','y'], tray: ['Y','Y','y','y']}
                               fill:   {boxes: [{i, value: 'Yy', options: ['YY','Yy','yy'], correct, hint}]}
                               numpad: {ask, value: 25, format: 'pct', answerText: '25%'}
                               choice: {options, correct}
                             `pool` = the bank.lab.json object (its `hints` templates); without it the hints are ''.
   Cross.check(step, answer, box?)  tiles: an array of 2 letters (either order: the game lays them capital first) ·
                             fill: the typed / picked genotype for box `box` (or an array of 4 for the whole square) ·
                             numpad: a number or typed "25" / "25%" · choice: the option index
   Cross.fade(k)             the fading for the k-th cross of an attempt (0-based; DESIGN §5.2, like Plot It):
                             {mode: 'guided'|'setup-done'|'answer-only', given: step types done for the student}
                             0–1 guided (every step) · 2–3 the tiles are placed for them · 4–5 only the percents, with
                             a "Show me the square" button
   Cross.byLevel(list)       a stable sort, easiest first (the game serves the draw in this order)

   ---- Seed Check (content/bank.check.json) ----
   Cross.CHEATS              ['wrongBox', 'genoAsPheno', 'countNotChance', 'blend', 'dominantCommon', 'recessiveGone']
   Cross.claim(text, item)   parses one claim sentence and judges it against the cross:
                             {kind, truth, stated, computed, value, ...} kinds: 'pct' ("25% … are expected to be green"),
                             'all' ("Every offspring will be Vv"), 'none' ("No kitten can be CC"), 'dominance' ("Tall (T) is
                             dominant over short (t)"), 'parents' ("Both parents have yellow seeds"), 'definition' ("Each
                             box is one possible offspring"), 'count' ("…exactly 5 will be white"), 'blend' ("…halfway
                             between…", "yellow-green"), 'dominantCommon' ("…dominant, so most … are …"), 'gone' ("The
                             constricted allele is gone …"). An unparsed sentence → {kind: 'unknown', truth: null}
                             (tools/crosscheck.js fails it: nothing is trusted).
   Cross.poster(item)        {id, cheat, trait, square (honest), shown (the boxes as drawn: wrongBox changes one),
                             badBox (-1 if none), claims: [{text, bad, verdict: Cross.claim(...)}], answer: {kind: 'box'|
                             'claim'|'none', index}}
   Cross.cheatHolds(item)    {ok, why}: the poster's mistake is false AND is the kind of mistake its cheat names
                             (DESIGN.md §4); a legit poster: every claim true, the square honest

   ---- the bank side ----
   Cross.pool(json)          a copy core/bank.js accepts (Bank.attempt / Bank.draw): lab items become fixed items (q =
                             the cross, options = the first ask's percent + two others, explain = the square); check items
                             get correct: 0 and explain = fix. The games never show these fields (they use steps() /
                             poster()); `_normalized` lists what changed.
   ========================================================================== */
(function (root) {
  const BANK = () => root.Bank || (typeof require === 'function' ? (() => { try { return require('../../../shared/engine/v2/core/bank.js'); } catch (e) { return null; } })() : null);

  /* ---------------- traits (docs/topics/facts-blue-eyes.md) ---------------- */
  const MENDEL = [
    { expt: 1, trait: 'seedShape', dominant: 'round', recessive: 'wrinkled', d: 5474, r: 1850, ratio: 2.96, unit: 'seeds' },
    { expt: 2, trait: 'seedColour', dominant: 'yellow', recessive: 'green', d: 6022, r: 2001, ratio: 3.01, unit: 'seeds' },
    { expt: 3, trait: 'flowerColour', dominant: 'violet', recessive: 'white', d: 705, r: 224, ratio: 3.15, unit: 'plants' },
    { expt: 4, trait: 'podShape', dominant: 'inflated', recessive: 'constricted', d: 882, r: 299, ratio: 2.95, unit: 'plants' },
    { expt: 5, trait: 'podColour', dominant: 'green', recessive: 'yellow', d: 428, r: 152, ratio: 2.82, unit: 'plants' },
    { expt: 6, trait: 'flowerPosition', dominant: 'axial', recessive: 'terminal', d: 651, r: 207, ratio: 3.14, unit: 'plants' },
    { expt: 7, trait: 'stemLength', dominant: 'tall', recessive: 'short', d: 787, r: 277, ratio: 2.84, unit: 'plants' },
  ].map(Object.freeze);
  MENDEL.total = Object.freeze({ d: 14949, r: 5010, ratio: 2.98, cite: 'facts §Safe numbers (all seven)' });
  Object.freeze(MENDEL);
  /* Mendel's single plants (facts 4.6): the square gives chances, not head counts */
  const SINGLE_PLANTS = Object.freeze([
    { trait: 'seedShape', d: 43, r: 2 }, { trait: 'seedShape', d: 14, r: 15 },
    { trait: 'seedColour', d: 32, r: 1 }, { trait: 'seedColour', d: 20, r: 19 },
  ].map(Object.freeze));
  const mendelOf = key => { const m = MENDEL.find(x => x.trait === key); return m ? { d: m.d, r: m.r, ratio: m.ratio, expt: m.expt, unit: m.unit } : null; };

  const TRAITS = {
    seedColour:   { gene: 'seed color', letter: 'Y', dominant: 'yellow', recessive: 'green', noun: 'seeds', of: 'offspring', pic: 'pea', cite: 'facts 2.11, 5.1' },
    seedShape:    { gene: 'seed shape', letter: 'R', dominant: 'round', recessive: 'wrinkled', noun: 'seeds', of: 'offspring', pic: 'peashape', cite: 'facts 2.11, 5.2' },
    flowerColour: { gene: 'flower color', letter: 'V', dominant: 'violet', recessive: 'white', noun: 'flowers', of: 'plants', pic: 'flower', cite: 'facts 2.11, 5.3' },
    podShape:     { gene: 'pod shape', letter: 'I', dominant: 'inflated', recessive: 'constricted', noun: 'pods', of: 'plants', pic: 'pod', cite: 'facts 5.4' },
    stemLength:   { gene: 'stem length', letter: 'T', dominant: 'tall', recessive: 'short', noun: 'plants', of: 'plants', pic: 'stem', cite: 'facts 2.11, 5.4' },
    catEar:       { gene: 'ear shape (made-up cat)', letter: 'C', dominant: 'curled', recessive: 'straight', noun: 'ears', of: 'kittens', pic: 'ear', madeUp: true, cite: 'facts 5.5 (FLDOE sample item 30)' },
    eyeModel:     { gene: 'eye color (simplified model)', letter: 'B', dominant: 'brown', recessive: 'blue', noun: 'eyes', of: 'children', pic: 'eye', simplified: true, cite: 'facts 2.6, 2.7, 5.6' },
  };
  for (const [k, t] of Object.entries(TRAITS)) { t.key = k; t.mendel = mendelOf(k); Object.freeze(t); }
  Object.freeze(TRAITS);

  const traitOf = g => {
    const up = String(g || '').toUpperCase();
    const hits = Object.values(TRAITS).filter(t => up && [...up].every(c => c === t.letter));
    return hits.length === 1 ? hits[0].key : null;
  };
  const T = key => { const t = TRAITS[key]; if (!t) throw new Error(`cross: unknown trait "${key}"`); return t; };
  const genotypes3 = key => { const L = T(key).letter, l = L.toLowerCase(); return [L + L, L + l, l + l]; };

  /* ---------------- one cross ---------------- */
  function norm(g) {
    const s = String(g || '');
    if (!/^[A-Za-z]{2}$/.test(s) || s[0].toUpperCase() !== s[1].toUpperCase()) throw new Error(`cross: "${g}" is not a one-gene genotype`);
    return [...s].sort((a, b) => (a === b ? 0 : a === a.toUpperCase() ? -1 : 1)).join('');
  }
  const gametes = g => [...norm(g)];
  const isDom = g => /[A-Z]/.test(g);
  function phenotype(key, g) { const t = T(key); return isDom(norm(g)) ? t.dominant : t.recessive; }
  function keyFor(p1, p2) {
    const k = traitOf(norm(p1)[0]);
    if (!k || traitOf(norm(p2)[0]) !== k) throw new Error(`cross: ${p1} × ${p2} are not the same gene`);
    return k;
  }
  function square(p1, p2, key) {
    key = key || keyFor(p1, p2);
    const top = gametes(p1), side = gametes(p2), boxes = [];
    for (let row = 0; row < 2; row++) for (let col = 0; col < 2; col++) {
      const g = norm(top[col] + side[row]);
      boxes.push({ i: row * 2 + col, row, col, top: top[col], side: side[row], genotype: g, phenotype: phenotype(key, g), dominant: isDom(g) });
    }
    return { key, p1: norm(p1), p2: norm(p2), top, side, boxes };
  }
  function genotypes(p1, p2) {
    const out = {};
    for (const b of square(p1, p2).boxes) out[b.genotype] = (out[b.genotype] || 0) + 25;
    return out;
  }
  function phenotypes(p1, p2) {
    const sq = square(p1, p2), t = TRAITS[sq.key], out = { [t.dominant]: 0, [t.recessive]: 0 };
    for (const b of sq.boxes) out[b.phenotype] += 25;
    return out;
  }
  function percent(p1, p2, { kind, value }) {
    const sq = square(p1, p2), t = TRAITS[sq.key];
    if (kind === 'geno') { const g = norm(value); if (traitOf(g[0]) !== sq.key) throw new Error(`cross: ${value} is not a ${t.gene} genotype`); return sq.boxes.filter(b => b.genotype === g).length * 25; }
    if (kind === 'pheno') { if (value !== t.dominant && value !== t.recessive) throw new Error(`cross: "${value}" is not a ${t.gene} phenotype (${t.dominant} / ${t.recessive})`); return sq.boxes.filter(b => b.phenotype === value).length * 25; }
    throw new Error(`cross: ask kind "${kind}" (geno | pheno)`);
  }

  /* seeded RNG (the same xmur3 → mulberry32 as core/bank.js, so a seed means the same thing everywhere) */
  function rngOf(seed) {
    const Bk = BANK();
    if (Bk && Bk.rng) return Bk.rng(seed);
    let s = String(seed), h = 1779033703 ^ s.length;
    for (let i = 0; i < s.length; i++) { h = Math.imul(h ^ s.charCodeAt(i), 3432918353); h = (h << 13) | (h >>> 19); }
    h = Math.imul(h ^ (h >>> 16), 2246822507); h = Math.imul(h ^ (h >>> 13), 3266489909);
    let a = (h ^ (h >>> 16)) >>> 0;
    return () => { a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
  }
  function grow(p1, p2, n, seed = 1) {
    const key = keyFor(p1, p2), a = gametes(p1), b = gametes(p2), r = rngOf(seed);
    const kids = [], counts = {}, pheno = { [TRAITS[key].dominant]: 0, [TRAITS[key].recessive]: 0 };
    for (let k = 0; k < n; k++) {
      const g = norm(a[r() < 0.5 ? 0 : 1] + b[r() < 0.5 ? 0 : 1]);
      kids.push(g); counts[g] = (counts[g] || 0) + 1; pheno[phenotype(key, g)]++;
    }
    return { n, seed, kids, counts, pheno };
  }
  function binom(n, k, pct) {
    const p = pct / 100;
    if (k < 0 || k > n) return 0;
    let c = 1; for (let i = 1; i <= k; i++) c = c * (n - k + i) / i;
    return c * Math.pow(p, k) * Math.pow(1 - p, n - k);
  }

  /* ---------------- Punnett Lab steps ---------------- */
  const fillT = (s, o) => String(s || '').replace(/\{(\w+)\}/g, (m, k) => (o[k] != null ? String(o[k]) : m));
  const pctText = v => `${v}%`;
  /* the question a percent ask reads (content has only {kind, value}; the wording is built here from the trait) */
  function askText(t, ask) {
    const who = t.of === 'offspring' ? 'the offspring' : `the ${t.of}`;
    if (ask.kind === 'geno') return `What percent of ${who} are expected to be ${ask.value}?`;
    const what = t.key === 'stemLength' ? `be ${ask.value}` : `have ${ask.value} ${t.noun}`;
    return `What percent of ${who} are expected to ${what}?`;
  }
  function steps(item, pool) {
    const t = T(item.trait), H = (pool && pool.hints) || {}, sq = square(item.p1, item.p2, item.trait);
    if (keyFor(item.p1, item.p2) !== item.trait) throw new Error(`cross.steps: ${item.id}: ${item.p1} × ${item.p2} is not ${item.trait}`);
    const L = t.letter, l = L.toLowerCase(), tray = [L, L, l, l];
    const out = [];
    const push = o => { o.i = out.length; out.push(o); return o; };
    push({ type: 'tiles', edge: 'top', parent: 1, value: sq.top.slice(), tray: tray.slice(), answerText: sq.p1,
      text: `Put the top parent's alleles (${sq.p1}) along the top.`, hint: fillT(H.tiles, { name: 'top', p: sq.p1 }) });
    push({ type: 'tiles', edge: 'side', parent: 2, value: sq.side.slice(), tray: tray.slice(), answerText: sq.p2,
      text: `Put the side parent's alleles (${sq.p2}) down the side.`, hint: fillT(H.tiles, { name: 'side', p: sq.p2 }) });
    const opts = genotypes3(item.trait);
    push({ type: 'fill', text: 'Fill each box: one letter from the top, one from the side.', hint: fillT(H.fill, { box: sq.boxes.map(b => b.genotype).join(', ') }),
      value: sq.boxes.map(b => b.genotype), answerText: sq.boxes.map(b => b.genotype).join(' · '),
      boxes: sq.boxes.map(b => ({ i: b.i, value: b.genotype, options: opts.slice(), correct: opts.indexOf(b.genotype), phenotype: b.phenotype,
        hint: fillT(H.fill, { box: `${b.top} from the top + ${b.side} from the side = ${b.genotype}` }) })) });
    for (const ask of item.ask || []) {
      if (ask.kind === 'choice') {
        push({ type: 'choice', ask, text: ask.q, options: ask.options.slice(), correct: ask.correct, value: ask.correct, answerText: ask.options[ask.correct], hint: ask.hint || '' });
        continue;
      }
      const v = percent(item.p1, item.p2, ask);
      const hint = ask.kind === 'geno' ? fillT(H.geno, { value: ask.value }) : fillT(H.pheno, { value: ask.value, dominant: t.dominant, recessive: t.recessive });
      push({ type: 'numpad', ask, text: askText(t, ask), hint, value: v, format: 'pct', answerText: pctText(v),
        boxes: sq.boxes.filter(b => (ask.kind === 'geno' ? b.genotype === norm(ask.value) : b.phenotype === ask.value)).map(b => b.i) });
    }
    return out;
  }
  const sameSet = (a, b) => a.length === b.length && [...a].sort().join() === [...b].sort().join();
  function parseTyped(v) {
    if (typeof v === 'number') return v;
    const s = String(v).replace(/[,\s%]/g, '').replace(/−/g, '-');
    return /^-?(\d+\.?\d*|\.\d+)$/.test(s) ? +s : NaN;
  }
  function check(step, answer, box) {
    if (!step) return false;
    if (step.type === 'tiles') return Array.isArray(answer) && sameSet(answer.map(String), step.value);
    if (step.type === 'fill') {
      const same = (a, want) => { try { return norm(a) === want; } catch (e) { return false; } };
      if (Array.isArray(answer)) return answer.length === 4 && answer.every((a, i) => same(a, step.value[i]));
      const b = step.boxes[box];
      if (!b) return false;
      return typeof answer === 'number' ? answer === b.correct : same(answer, b.value);
    }
    if (step.type === 'numpad') { const v = parseTyped(answer); return isFinite(v) && v === step.value; }
    if (step.type === 'choice') return answer === step.correct;
    return false;
  }
  function fade(k) {
    if (k <= 1) return { mode: 'guided', given: [] };
    if (k <= 3) return { mode: 'setup-done', given: ['tiles'] };
    return { mode: 'answer-only', given: ['tiles', 'fill'], showSquare: true };
  }
  const byLevel = list => list.map((x, i) => [x, i]).sort((a, b) => ((a[0].item || a[0]).level || 0) - ((b[0].item || b[0]).level || 0) || a[1] - b[1]).map(p => p[0]);

  /* ---------------- Seed Check: claims and posters ---------------- */
  const CHEATS = Object.freeze(['wrongBox', 'genoAsPheno', 'countNotChance', 'blend', 'dominantCommon', 'recessiveGone']);
  const DEFINITIONS = Object.freeze(['Each box is one possible offspring.']);
  const NOUNS = /\s+(seeds?|flowers?|pods?|plants?|ears?|eyes?|stems?)$/i;
  /* "green" / "curled ears" / "cc (straight ears)" / "Yy" → {geno} or {pheno} for the cross's trait */
  function target(phrase, key) {
    const t = TRAITS[key];
    let s = String(phrase).trim(), paren = null;
    const m = /^(.*?)\s*\(([^)]*)\)$/.exec(s);
    if (m) { s = m[1]; paren = m[2]; }
    if (/^[A-Za-z]{2}$/.test(s) && traitOf(s.toUpperCase()[0]) === key && s.toUpperCase()[0] === s.toUpperCase()[1]) {
      const g = norm(s);
      let parenOk = true;
      if (paren != null) parenOk = paren.replace(NOUNS, '').trim() === phenotype(key, g);
      return { geno: g, parenOk };
    }
    s = s.replace(NOUNS, '').trim();
    if (s === t.dominant || s === t.recessive) return { pheno: s };
    return null;
  }
  const lc = s => String(s).trim().replace(/^(So|And)\s+/, '').replace(/^In this made-up cat,\s*/i, '').trim();
  function claim(text, item) {
    const key = item.trait, t = T(key), p1 = item.p1, p2 = item.p2, s = lc(text);
    const pc = spec => percent(p1, p2, spec);
    let m;
    const out = (o) => Object.assign({ text }, o);
    if (DEFINITIONS.includes(s)) return out({ kind: 'definition', truth: true });
    // "25% of the offspring are expected to be green." / "50% are expected to be Rr." / "75% are expected to be yellow."
    if ((m = /^(\d+)% (?:of (?:the )?(\w+) )?(?:are|is) expected to (?:be|have) (.+?)\.$/.exec(s))) {
      const stated = +m[1], tg = target(m[3], key);
      if (!tg) return out({ kind: 'unknown', truth: null, why: `can't read "${m[3]}" as a ${t.gene} genotype or phenotype` });
      const spec = tg.geno ? { kind: 'geno', value: tg.geno } : { kind: 'pheno', value: tg.pheno };
      const computed = pc(spec);
      return out({ kind: 'pct', spec, stated, computed, truth: stated === computed && tg.parenOk !== false, parenOk: tg.parenOk });
    }
    // "Every offspring will be Vv."
    if ((m = /^Every (\w+) will be (\w+)\.$/.exec(s))) {
      const tg = target(m[2], key); if (!tg) return out({ kind: 'unknown', truth: null });
      const spec = tg.geno ? { kind: 'geno', value: tg.geno } : { kind: 'pheno', value: tg.pheno };
      const computed = pc(spec);
      return out({ kind: 'all', spec, stated: 100, computed, truth: computed === 100 });
    }
    // "No kitten can be CC." / "none of the offspring can have green seeds."
    if ((m = /^(?:No \w+|[Nn]one of the \w+) can (?:be|have) (.+?)\.$/.exec(s))) {
      const tg = target(m[1], key); if (!tg) return out({ kind: 'unknown', truth: null });
      const spec = tg.geno ? { kind: 'geno', value: tg.geno } : { kind: 'pheno', value: tg.pheno };
      const computed = pc(spec);
      return out({ kind: 'none', spec, stated: 0, computed, truth: computed === 0 });
    }
    // "Tall (T) is dominant over short (t)."
    if ((m = /^(\w+) \((\w)\) is dominant over (\w+) \((\w)\)\.$/i.exec(s))) {
      const truth = m[1].toLowerCase() === t.dominant && m[2] === t.letter && m[3].toLowerCase() === t.recessive && m[4] === t.letter.toLowerCase();
      return out({ kind: 'dominance', truth });
    }
    // "Both parents have yellow seeds." / "Both parents have inflated pods."
    if ((m = /^Both parents (?:have|are) (.+?)\.$/.exec(s))) {
      const word = m[1].replace(NOUNS, '').trim();
      return out({ kind: 'parents', value: word, truth: phenotype(key, p1) === word && phenotype(key, p2) === word });
    }
    // "…exactly 1 wrinkled seed." / "We grew 10 plants, so exactly 5 will be white."
    if ((m = /(?:of|grew) (\d+) (\w+).*?exactly (\d+) (.+?)\.$/.exec(s))) {
      const n = +m[1], k = +m[3];
      let phrase = m[4].replace(/^will be\s+/, '').replace(/\s+(seed|plant|kitten)s?$/, '');
      const tg = target(phrase, key); if (!tg) return out({ kind: 'unknown', truth: null, why: `count of "${m[4]}"` });
      const spec = tg.geno ? { kind: 'geno', value: tg.geno } : { kind: 'pheno', value: tg.pheno };
      const pct = pc(spec), certainty = binom(n, k, pct);
      return out({ kind: 'count', spec, n, k, pct, expected: n * pct / 100, certainty, truth: certainty === 1 });
    }
    // blending: "…pale, halfway between violet and white." / "the seeds will be yellow-green."
    if (/halfway between|\bblend|\bmix/.test(s) || new RegExp(`\\b(${t.dominant}-${t.recessive}|${t.recessive}-${t.dominant})\\b`).test(s)) {
      return out({ kind: 'blend', truth: false, why: 'one gene, simple dominance: the offspring show one phenotype or the other (facts 6.4)' });
    }
    // "Tall is dominant, so most pea plants in the world are tall." / "Curled ears are dominant, so they must be the most common …"
    if ((m = /^(\w+)(?: \w+)? (?:is|are) dominant, so .*\b(most|more common)\b/.exec(s))) {
      return out({ kind: 'dominantCommon', value: m[1].toLowerCase(), namesDominant: m[1].toLowerCase() === t.dominant, truth: false,
        why: 'dominant = shows with one copy; it says nothing about how common a trait is (facts 6.1)' });
    }
    // "The constricted allele is gone from this family for good."
    if ((m = /^The (\w+) allele is gone/.exec(s))) {
      const word = m[1].toLowerCase(), allele = word === t.recessive ? t.letter.toLowerCase() : word === t.dominant ? t.letter : null;
      if (!allele) return out({ kind: 'unknown', truth: null });
      const carried = gametes(p1).includes(allele) || gametes(p2).includes(allele);
      return out({ kind: 'gone', allele, carried, truth: !carried });
    }
    return out({ kind: 'unknown', truth: null, why: 'no claim pattern matches: cross.js cannot verify it' });
  }
  function poster(item) {
    const sq = square(item.p1, item.p2, item.trait);
    const shown = sq.boxes.map(b => ({ ...b }));
    let badBox = -1;
    if (item.cheat === 'wrongBox') {
      badBox = item.badBox;
      const g = norm(item.shown);
      shown[badBox] = { ...shown[badBox], genotype: g, phenotype: phenotype(item.trait, g), dominant: isDom(g), wrong: true };
    }
    const claims = (item.claims || []).map((c, i) => ({ i, text: c.text, bad: !!c.bad, verdict: claim(c.text, item) }));
    const badClaim = claims.findIndex(c => c.bad);
    const answer = item.cheat === 'wrongBox' ? { kind: 'box', index: badBox } : badClaim >= 0 ? { kind: 'claim', index: badClaim } : { kind: 'none', index: -1 };
    return { id: item.id, cheat: item.cheat, trait: item.trait, madeUp: !!TRAITS[item.trait].madeUp, square: sq, shown, badBox, claims, answer };
  }
  /* is the poster's mistake false, and the kind of mistake its cheat names? (DESIGN.md §4) */
  function cheatHolds(item) {
    const P = poster(item), sq = P.square, bad = P.claims.filter(c => c.bad), good = P.claims.filter(c => !c.bad);
    const t = TRAITS[item.trait];
    const unread = P.claims.filter(c => c.verdict.truth == null);
    if (unread.length) return { ok: false, why: `claim(s) cross.js can't verify: ${unread.map(c => `"${c.text}"`).join(', ')}` };
    const falseGood = good.filter(c => c.verdict.truth !== true);
    if (falseGood.length) return { ok: false, why: `claim(s) not marked bad are false: ${falseGood.map(c => `"${c.text}" (${JSON.stringify({ stated: c.verdict.stated, computed: c.verdict.computed })})`).join(', ')}` };
    if (item.cheat === 'legit') return bad.length || P.badBox >= 0 ? { ok: false, why: 'a legit poster has a bad claim / box' } : { ok: true, why: 'every claim true, the square honest' };
    if (item.cheat === 'wrongBox') {
      if (bad.length) return { ok: false, why: 'a wrongBox poster must have all claims true' };
      const b = sq.boxes[item.badBox];
      if (!b) return { ok: false, why: `badBox ${item.badBox} is not 0–3` };
      let g; try { g = norm(item.shown); } catch (e) { return { ok: false, why: e.message }; }
      if (traitOf(g[0]) !== item.trait) return { ok: false, why: `shown ${item.shown} is not a ${t.gene} genotype` };
      return g !== b.genotype ? { ok: true, why: `box ${item.badBox} shows ${g}, the model gives ${b.genotype}` } : { ok: false, why: `box ${item.badBox} shown ${g} IS the honest genotype` };
    }
    if (bad.length !== 1) return { ok: false, why: `exactly one bad claim expected, found ${bad.length}` };
    const v = bad[0].verdict;
    if (v.truth !== false) return { ok: false, why: `the bad claim "${bad[0].text}" is TRUE in the model` };
    if (item.cheat === 'genoAsPheno') {
      if (v.kind !== 'pct' || !v.spec || v.spec.kind !== 'pheno') return { ok: false, why: 'genoAsPheno: the bad claim must state a phenotype percent' };
      const G = genotypes(item.p1, item.p2);
      const as = Object.entries(G).filter(([g, p]) => p === v.stated && phenotype(item.trait, g) === v.spec.value);
      return as.length ? { ok: true, why: `"${v.spec.value}" stated as ${v.stated}% = the ${as.map(a => a[0]).join('/')} genotype percent; the phenotype is ${v.computed}%` }
        : { ok: false, why: `${v.stated}% is not the percent of a ${v.spec.value} genotype (${JSON.stringify(G)})` };
    }
    if (item.cheat === 'countNotChance') {
      if (v.kind !== 'count') return { ok: false, why: 'countNotChance: the bad claim must state an exact count' };
      return v.pct > 0 && v.pct < 100 && v.k === v.expected ? { ok: true, why: `exactly ${v.k} of ${v.n} = ${v.pct}% taken as a count; P(exactly ${v.k}) = ${(v.certainty * 100).toFixed(1)}%` }
        : { ok: false, why: `count ${v.k} of ${v.n} vs chance ${v.pct}%: not the chance-as-count mistake` };
    }
    if (item.cheat === 'blend') {
      if (v.kind !== 'blend') return { ok: false, why: 'blend: the bad claim must describe a blended phenotype' };
      return sq.boxes.some(b => b.genotype[0] !== b.genotype[1]) ? { ok: true, why: 'heterozygous offspring show the dominant phenotype, not a blend' } : { ok: false, why: 'no heterozygous offspring in this cross' };
    }
    if (item.cheat === 'dominantCommon') {
      if (v.kind !== 'dominantCommon') return { ok: false, why: 'dominantCommon: the bad claim must infer "common" from "dominant"' };
      return v.namesDominant ? { ok: true, why: `${v.value} IS dominant; "so it's the most common" does not follow (facts 6.1)` } : { ok: false, why: `"${v.value}" is not the dominant phenotype` };
    }
    if (item.cheat === 'recessiveGone') {
      const recessive = t.recessive;
      if (v.kind === 'none' && v.spec.kind === 'pheno' && v.spec.value === recessive) return v.computed > 0 ? { ok: true, why: `${recessive} is ${v.computed}% expected, not 0` } : { ok: false, why: 'the recessive phenotype really is 0%' };
      if (v.kind === 'gone') return v.carried ? { ok: true, why: `the ${v.allele} allele is carried by a parent` } : { ok: false, why: 'no parent carries it' };
      return { ok: false, why: 'recessiveGone: the bad claim must say the recessive trait / allele cannot come back' };
    }
    return { ok: false, why: `unknown cheat "${item.cheat}"` };
  }

  /* ---------------- pools for core/bank.js ---------------- */
  function pool(json) {
    const out = JSON.parse(JSON.stringify(json));
    for (const it of out.items || []) {
      const changed = [];
      if (out.pool === 'lab' || (it.p1 && it.p2 && it.ask && !it.q)) {
        const first = (it.ask || []).find(a => a.kind === 'geno' || a.kind === 'pheno');
        const v = first ? percent(it.p1, it.p2, first) : 0;
        const others = [0, 25, 50, 75, 100].filter(x => x !== v);
        it.q = `${TRAITS[it.trait].gene}: ${norm(it.p1)} × ${norm(it.p2)}`;
        it.options = [pctText(v), pctText(others[0]), pctText(others[others.length - 1])];
        it.correct = 0;
        it.explain = 'Boxes: ' + square(it.p1, it.p2, it.trait).boxes.map(b => b.genotype).join(', ');
        changed.push('lab cross → fixed item (q, options, correct, explain from the model; never shown)');
      }
      if (it.claims && it.correct === undefined) { it.correct = 0; changed.push('correct: 0 (the pool\'s notes)'); }
      if (it.claims && it.explain === undefined && it.fix) { it.explain = it.fix; changed.push('explain = fix'); }
      if (changed.length) it._normalized = changed;
    }
    return out;
  }

  const Cross = {
    TRAITS, MENDEL, SINGLE_PLANTS, traitOf, genotypes3,
    norm, gametes, phenotype, square, genotypes, phenotypes, percent, grow, binom, rng: rngOf,
    steps, check, parseTyped, fade, byLevel, askText,
    CHEATS, DEFINITIONS, claim, poster, cheatHolds,
    pool,
  };
  root.Cross = Cross;
  const Bk = root.Bank;
  if (Bk && typeof Bk.register === 'function') Bk.register('cross', Cross);
  if (typeof module !== 'undefined' && module.exports) module.exports = Cross;
})(typeof window !== 'undefined' ? window : globalThis);
